import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import useSWR from "swr";
import { useCampaign } from "../views/Campaign/CampaignContext";
import { usePlace } from "./PlaceProvider";
import { HTTPClient } from "../HTTPClient";
import dayjs from "dayjs";

interface ToDoContextValue {
  jeuDone: boolean;
  googleConnected: boolean | null;
  hasCampaigns: boolean | null;
  googleIaSetupDone: boolean | null;
  googleIaAnalysisDone: boolean | null;
  googleIaPrevMonthAvailable: boolean | null;
  googleIaCurrentMonthDone: boolean | null;
  prevMonthReady: boolean | null;
  pendingCount: number;
  totalDone: number;
  total: number;
  isLoading: boolean;
  refresh: () => void;
}

const ToDoContext = createContext<ToDoContextValue>({
  jeuDone: false,
  googleConnected: null,
  hasCampaigns: null,
  googleIaSetupDone: null,
  googleIaAnalysisDone: null,
  googleIaPrevMonthAvailable: null,
  googleIaCurrentMonthDone: null,
  prevMonthReady: null,
  pendingCount: 0,
  totalDone: 2,
  total: 7,
  isLoading: true,
  refresh: () => {},
});

function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function ToDoProvider({ children }: { children: ReactNode }) {
  const { isLoaded, actions, gifts } = useCampaign();
  const { selectedPlace } = usePlace();

  const jeuDone = isLoaded ? actions.length > 0 || gifts.length > 0 : false;
  const [googleConnected, setGoogleConnected] = useState<boolean | null>(null);
  const [hasCampaigns, setHasCampaigns] = useState<boolean | null>(null);
  const [googleIaSetupDone, setGoogleIaSetupDone] = useState<boolean | null>(null);
  const [googleIaAnalysisDone, setGoogleIaAnalysisDone] = useState<boolean | null>(null);
  const [googleIaPrevMonthAvailable, setGoogleIaPrevMonthAvailable] = useState<boolean | null>(null);
  const [googleIaCurrentMonthDone, setGoogleIaCurrentMonthDone] = useState<boolean | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const prevPlaceIdRef = useRef<string | null>(null);

  const refresh = () => setRefreshKey((k) => k + 1);

  const prevMonthKey = dayjs().subtract(1, "month").format("YYYY-MM");

  // Readiness via SWR — clé incluant la date du jour → invalide automatiquement à minuit
  const readinessSWRKey = selectedPlace?.id
    ? `readiness:${selectedPlace.id}:${prevMonthKey}:${todayKey()}`
    : null;

  const { data: readinessData } = useSWR(
    readinessSWRKey,
    () => HTTPClient.get(`/stats/google/keyword-insights/readiness?placeId=${selectedPlace!.id}&monthKey=${prevMonthKey}`)
      .then(({ data }) => data?.ready === true)
      .catch(() => false),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
    }
  );

  const prevMonthReady = readinessSWRKey ? (readinessData ?? null) : null;

  useEffect(() => {
    const isPlaceChange = selectedPlace?.id !== prevPlaceIdRef.current;
    prevPlaceIdRef.current = selectedPlace?.id ?? null;

    if (!selectedPlace?.id) {
      setGoogleConnected(null);
      setHasCampaigns(null);
      setGoogleIaSetupDone(null);
      setGoogleIaAnalysisDone(null);
      setGoogleIaPrevMonthAvailable(null);
      setGoogleIaCurrentMonthDone(null);
      return;
    }

    if (isPlaceChange) {
      setGoogleConnected(null);
      setHasCampaigns(null);
      setGoogleIaSetupDone(null);
      setGoogleIaAnalysisDone(null);
      setGoogleIaPrevMonthAvailable(null);
      setGoogleIaCurrentMonthDone(null);
    }

    const placeId = selectedPlace.id;

    HTTPClient.get("/reviews/config?id=" + placeId)
      .then(({ data }) => setGoogleConnected(!!data?.google))
      .catch(() => setGoogleConnected(false));
    HTTPClient.get("/sms?id=" + placeId)
      .then(({ data }) => setHasCampaigns(Array.isArray(data) && data.length > 0))
      .catch(() => setHasCampaigns(false));
    HTTPClient.get(`/stats/google/keyword-setup?placeId=${placeId}`)
      .then(() => setGoogleIaSetupDone(true))
      .catch(() => setGoogleIaSetupDone(false));

    const currentMonthKey = dayjs().format("YYYY-MM");
    const start = dayjs().subtract(6, "month").format("YYYY-MM");

    HTTPClient.get(`/stats/google/keyword-insights/history?placeId=${placeId}&startMonth=${start}&endMonth=${currentMonthKey}`)
      .then(({ data }) => {
        const snapshots: { monthKey: string }[] = data?.snapshots ?? [];
        const keys = new Set(snapshots.map((s) => s.monthKey));
        setGoogleIaAnalysisDone(snapshots.length > 0);
        setGoogleIaPrevMonthAvailable(keys.has(prevMonthKey));
        setGoogleIaCurrentMonthDone(keys.has(currentMonthKey));
      })
      .catch(() => {
        setGoogleIaAnalysisDone(false);
        setGoogleIaPrevMonthAvailable(false);
        setGoogleIaCurrentMonthDone(false);
      });
  }, [selectedPlace?.id, refreshKey]);

  const isLoading = !isLoaded || googleConnected === null || hasCampaigns === null || googleIaSetupDone === null || googleIaAnalysisDone === null;

  const TOTAL_ACTIONS = 8;
  const doneCount = [
    true,
    true,
    jeuDone,
    googleConnected === true,
    hasCampaigns === true,
    googleIaSetupDone === true,
    googleIaAnalysisDone === true,
    googleIaPrevMonthAvailable === true,
  ].filter(Boolean).length;
  const pendingCount = TOTAL_ACTIONS - doneCount;

  const totalDone = doneCount;
  const total = TOTAL_ACTIONS;

  return (
    <ToDoContext.Provider value={{ jeuDone, googleConnected, hasCampaigns, googleIaSetupDone, googleIaAnalysisDone, googleIaPrevMonthAvailable, googleIaCurrentMonthDone, prevMonthReady, pendingCount, totalDone, total, isLoading, refresh }}>
      {children}
    </ToDoContext.Provider>
  );
}

export function useToDo() {
  return useContext(ToDoContext);
}
